import { Component, type ErrorInfo, type ReactNode } from 'react';

import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the default panel when a render error is caught. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors below it in the tree.
 *
 * React Router handles errors thrown by loaders and route elements; this
 * covers everything else, including errors thrown by a provider that sits
 * above the router.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;

    if (error === null) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div className={styles.panel} role="alert">
        <h2 className={styles.title}>Something went wrong</h2>
        <p className={styles.message}>
          The page could not be displayed. You can try again, and if the problem continues please
          contact support.
        </p>
        <button type="button" className={styles.button} onClick={this.reset}>
          Try again
        </button>
      </div>
    );
  }
}
